import html2canvas from 'html2canvas'
import jsPDF from 'jspdf'
import { useSlidesStore } from '~/stores/slides'

export const usePDFExport = () => {
  const slidesStore = useSlidesStore()
  const isExporting = ref(false)
  const exportProgress = ref(0)

  const exportToPDF = async () => {
    try {
      isExporting.value = true
      exportProgress.value = 0

      // Create a new jsPDF instance
      const pdf = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: 'a4'
      })

      // PDF dimensions (A4 landscape)
      const pdfWidth = 297
      const pdfHeight = 210

      // Create a temporary container for rendering slides
      const tempContainer = document.createElement('div')
      tempContainer.style.position = 'fixed'
      tempContainer.style.top = '-9999px'
      tempContainer.style.left = '-9999px'
      tempContainer.style.width = '1920px'
      tempContainer.style.height = '1080px'
      tempContainer.style.backgroundColor = '#ffffff'
      tempContainer.style.zIndex = '-1000'
      document.body.appendChild(tempContainer)

      // Note: PDF export renders slide content directly from slide data
      // No need to import Vue components for PDF generation

      // Process each slide
      for (let i = 0; i < slidesStore.slides.length; i++) {
        const slide = slidesStore.slides[i]
        exportProgress.value = Math.round((i / slidesStore.slides.length) * 100)

        // Create slide element
        const slideElement = document.createElement('div')
        slideElement.style.width = '1920px'
        slideElement.style.height = '1080px'
        slideElement.style.padding = '60px'
        slideElement.style.boxSizing = 'border-box'
        slideElement.style.backgroundColor = '#ffffff'
        slideElement.style.fontFamily = 'system-ui, -apple-system, sans-serif'
        slideElement.style.color = '#000000'
        
        // Add slide content based on slide type
        await renderSlideContent(slideElement, slide)
        
        tempContainer.appendChild(slideElement)

        // Wait for any animations or async content to load
        await new Promise(resolve => setTimeout(resolve, 1000))

        // Capture the slide as canvas
        const canvas = await html2canvas(slideElement, {
          width: 1920,
          height: 1080,
          scale: 1,
          useCORS: true,
          allowTaint: true,
          backgroundColor: '#ffffff',
          logging: false
        })

        // Convert canvas to image
        const imgData = canvas.toDataURL('image/png', 1.0)

        // Add page to PDF (except for first slide)
        if (i > 0) {
          pdf.addPage()
        }

        // Add image to PDF
        pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight)

        // Clean up
        tempContainer.removeChild(slideElement)
      }

      // Clean up temporary container
      document.body.removeChild(tempContainer)

      // Generate filename with current date
      const now = new Date()
      const dateStr = now.toISOString().split('T')[0]
      const filename = `BRX-ai-Pitch-Deck-${dateStr}.pdf`

      // Save the PDF
      pdf.save(filename)

      exportProgress.value = 100
      
      // Show success message
      console.log('PDF exported successfully!')
      
    } catch (error) {
      console.error('Error exporting PDF:', error)
      throw error
    } finally {
      isExporting.value = false
      setTimeout(() => {
        exportProgress.value = 0
      }, 2000)
    }
  }

  const renderSlideContent = async (element: HTMLElement, slide: any) => {
    // Create slide content based on slide data
    const slideContent = document.createElement('div')
    slideContent.style.height = '100%'
    slideContent.style.display = 'flex'
    slideContent.style.flexDirection = 'column'
    slideContent.style.justifyContent = 'center'
    slideContent.style.padding = '40px'

    // Add title
    const titleField = slide.fields.find((f: any) => f.type === 'title')
    if (titleField) {
      const title = document.createElement('h1')
      title.textContent = titleField.value
      title.style.fontSize = '48px'
      title.style.fontWeight = 'bold'
      title.style.marginBottom = '24px'
      title.style.textAlign = 'center'
      title.style.background = getSlideGradient(slide.id)
      title.style.webkitBackgroundClip = 'text'
      title.style.webkitTextFillColor = 'transparent'
      slideContent.appendChild(title)
    }

    // Add subtitle
    const subtitleField = slide.fields.find((f: any) => f.type === 'subtitle')
    if (subtitleField) {
      const subtitle = document.createElement('h2')
      subtitle.textContent = subtitleField.value
      subtitle.style.fontSize = '24px'
      subtitle.style.fontWeight = '600'
      subtitle.style.marginBottom = '32px'
      subtitle.style.textAlign = 'center'
      subtitle.style.color = '#6b7280'
      slideContent.appendChild(subtitle)
    }

    // Add content
    const contentField = slide.fields.find((f: any) => f.type === 'content')
    if (contentField) {
      const content = document.createElement('p')
      content.textContent = contentField.value
      content.style.fontSize = '18px'
      content.style.lineHeight = '1.6'
      content.style.marginBottom = '32px'
      content.style.textAlign = 'center'
      content.style.maxWidth = '800px'
      content.style.margin = '0 auto 32px auto'
      slideContent.appendChild(content)
    }

    // Add bullet points
    const bulletField = slide.fields.find((f: any) => f.type === 'bulletPoints')
    if (bulletField && Array.isArray(bulletField.value)) {
      const bulletContainer = document.createElement('div')
      bulletContainer.style.maxWidth = '900px'
      bulletContainer.style.margin = '0 auto'
      
      bulletField.value.forEach((bullet: string) => {
        const bulletItem = document.createElement('div')
        bulletItem.style.display = 'flex'
        bulletItem.style.alignItems = 'flex-start'
        bulletItem.style.marginBottom = '16px'
        bulletItem.style.padding = '16px'
        bulletItem.style.backgroundColor = '#f8fafc'
        bulletItem.style.borderRadius = '8px'
        bulletItem.style.border = '1px solid #e2e8f0'

        const bulletPoint = document.createElement('span')
        bulletPoint.textContent = '•'
        bulletPoint.style.color = getSlideColor(slide.id)
        bulletPoint.style.fontWeight = 'bold'
        bulletPoint.style.marginRight = '12px'
        bulletPoint.style.fontSize = '18px'

        const bulletText = document.createElement('span')
        bulletText.textContent = bullet
        bulletText.style.fontSize = '16px'
        bulletText.style.lineHeight = '1.5'
        bulletText.style.flex = '1'

        bulletItem.appendChild(bulletPoint)
        bulletItem.appendChild(bulletText)
        bulletContainer.appendChild(bulletItem)
      })
      
      slideContent.appendChild(bulletContainer)
    }

    element.appendChild(slideContent)
  }

  const getSlideGradient = (slideId: string) => {
    const gradients: Record<string, string> = {
      'title': 'linear-gradient(to right, #3b82f6, #8b5cf6)',
      'problem': 'linear-gradient(to right, #ef4444, #f97316)',
      'solution': 'linear-gradient(to right, #10b981, #3b82f6)',
      'differentiator': 'linear-gradient(to right, #f59e0b, #f97316)',
      'vision': 'linear-gradient(to right, #8b5cf6, #ec4899)',
      'market': 'linear-gradient(to right, #f97316, #ef4444)',
      'business-model': 'linear-gradient(to right, #10b981, #14b8a6)',
      'adoption': 'linear-gradient(to right, #22c55e, #10b981)',
      'social-proof': 'linear-gradient(to right, #8b5cf6, #6366f1)',
      'team': 'linear-gradient(to right, #3b82f6, #8b5cf6)',
      'advisors': 'linear-gradient(to right, #6366f1, #8b5cf6)',
      'closing': 'linear-gradient(to right, #3b82f6, #06b6d4)'
    }
    return gradients[slideId] || 'linear-gradient(to right, #3b82f6, #8b5cf6)'
  }

  const getSlideColor = (slideId: string) => {
    const colors: Record<string, string> = {
      'title': '#3b82f6',
      'problem': '#ef4444',
      'solution': '#10b981',
      'differentiator': '#f59e0b',
      'vision': '#8b5cf6',
      'market': '#f97316',
      'business-model': '#10b981',
      'adoption': '#22c55e',
      'social-proof': '#8b5cf6',
      'team': '#3b82f6',
      'advisors': '#6366f1',
      'closing': '#3b82f6'
    }
    return colors[slideId] || '#3b82f6'
  }

  return {
    exportToPDF,
    isExporting: readonly(isExporting),
    exportProgress: readonly(exportProgress)
  }
}
